import { EMPTY_LABEL } from '@/components/challenge/CompanyChallengeManagement/constants';
import { IChallengeFormValues } from '@/components/challenge/CompanyChallengeManagement/types';
import useAppDispatch from '@/hooks/useAppDispatch';
import { getCompanyChallenge } from '@/redux/actions/challenge';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

/**
 * Fetches the company's challenge config once on mount and seeds the form's
 * `initialValues` from it — falling back to a blank config (create flow)
 * if none exists yet. Also tracks the highest label index handed out so
 * far, since new labels must never reuse a freed index (see the label
 * array-diffing note in the plan/spec).
 */
export function useCompanyChallengeInitialValues(companyId: number) {
  const dispatch = useAppDispatch();

  const maxUsedLabelIndex = useRef(-1);

  const [initialValues, setInitialValues] =
    useState<IChallengeFormValues | null>(null);

  // Seeds directly off this fetch's own result, not off the redux loading
  // flag — no row yet (fetch resolves to null) means Save acts as a create
  // instead of an update.
  useEffect(() => {
    dispatch(getCompanyChallenge({ companyId }))
      .unwrap()
      .then((companyChallenge) => {
        const labels = companyChallenge?.labels?.length
          ? companyChallenge.labels
          : [EMPTY_LABEL];

        maxUsedLabelIndex.current = Math.max(
          ...labels.map((label) => label.index)
        );

        setInitialValues({
          title: companyChallenge?.title ?? '',
          description: companyChallenge?.description ?? '',
          labels,
        });
      })
      .catch((err) => {
        toast.error(err?.error ?? 'Failed to load challenge configuration', {
          position: 'top-right',
        });
      });
  }, [dispatch, companyId]);

  return { initialValues, maxUsedLabelIndex };
}
